import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  getAsn,
  listAsnLines,
  listAsnSkus,
  recordReceipt,
  recordAsnAccessorials,
} from "@/lib/receiving.functions";
import { receiveSerials } from "@/lib/fulfillment.functions";
import { lookupLocationByCode } from "@/lib/putaway-directives.functions";
import { RfShell, RfEmpty } from "@/components/rf/rf-shell";
import { ScanInput } from "@/components/ui/scan-input";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useScope } from "@/hooks/use-scope";
import { toast } from "sonner";
import { CheckCircle2, Clock } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/rf/receiving/$asnId")({
  component: RfReceivingAsn,
});

type Step = "line" | "location" | "qty" | "serials";

function RfReceivingAsn() {
  const { asnId } = Route.useParams();
  const { tenantId, facilityId } = useScope();
  const nav = useNavigate();
  const qc = useQueryClient();

  const getAsnFn = useServerFn(getAsn);
  const listLinesFn = useServerFn(listAsnLines);
  const listSkusFn = useServerFn(listAsnSkus);
  const lookupLocFn = useServerFn(lookupLocationByCode);
  const recordFn = useServerFn(recordReceipt);
  const serialsFn = useServerFn(receiveSerials);
  const accessorialsFn = useServerFn(recordAsnAccessorials);

  const { data: asn } = useQuery({
    queryKey: ["rf-asn", asnId],
    queryFn: () => getAsnFn({ data: { asnId } }),
  });
  const { data: lines = [], refetch } = useQuery({
    queryKey: ["rf-asn-lines", asnId],
    queryFn: () => listLinesFn({ data: { asnId } }),
  });
  const { data: skus = [] } = useQuery({
    queryKey: ["rf-asn-skus", (asn as any)?.customer_id],
    enabled: !!(asn as any)?.customer_id,
    queryFn: () =>
      listSkusFn({
        data: { customerId: (asn as any).customer_id, tenantId: tenantId!, activeOnly: false },
      }),
  });

  const skuById = useMemo(() => {
    const m = new Map<string, any>();
    for (const s of skus as any[]) m.set(s.id, s);
    return m;
  }, [skus]);

  const [step, setStep] = useState<Step>("line");
  const [lineId, setLineId] = useState<string | null>(null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [locationCode, setLocationCode] = useState("");
  const [qty, setQty] = useState("");
  const [casesQty, setCasesQty] = useState("");
  const [palletsQty, setPalletsQty] = useState("");
  const [damageQty, setDamageQty] = useState("");
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingReceiptLineId, setPendingReceiptLineId] = useState<string | null>(null);
  const [serials, setSerials] = useState<string[]>([]);
  const [serialsTarget, setSerialsTarget] = useState(0);

  const activeLine: any = lines.find((l: any) => l.id === lineId) ?? null;
  const activeSku = activeLine ? skuById.get(activeLine.sku_id) : null;

  function reset() {
    setStep("line");
    setLineId(null);
    setLocationId(null);
    setLocationCode("");
    setQty("");
    setCasesQty("");
    setPalletsQty("");
    setDamageQty("");
    setExpiry("");
    setPendingReceiptLineId(null);
    setSerials([]);
    setSerialsTarget(0);
  }

  function pickLine(id: string) {
    setLineId(id);
    setStep("location");
  }

  async function handleScan(code: string) {
    if (step === "line") {
      const match = (lines as any[]).find((l) => {
        const sku = skuById.get(l.sku_id);
        return (
          sku?.sku_code?.toLowerCase() === code.toLowerCase() ||
          (l.lot ?? "").toLowerCase() === code.toLowerCase()
        );
      });
      if (!match) return toast.error(`No line matches "${code}"`);
      pickLine(match.id);
      return;
    }
    if (step === "location") {
      try {
        const loc = await lookupLocFn({
          data: { tenantId: tenantId!, facilityId: facilityId!, code: code.trim() },
        });
        if (!loc) return toast.error(`Unknown location "${code}"`);
        setLocationId((loc as any).id);
        setLocationCode((loc as any).code);
        setStep("qty");
      } catch (e: any) {
        toast.error(e?.message ?? "Lookup failed");
      }
    }
  }

  async function confirm() {
    if (!lineId || !locationId) return;
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Enter a positive quantity");
    const dmg = damageQty.trim() ? parseInt(damageQty, 10) : 0;
    if (!Number.isFinite(dmg) || dmg < 0) return toast.error("Damage qty must be ≥ 0");
    if (dmg > n) return toast.error("Damage qty cannot exceed total qty");
    const cases = casesQty.trim() ? parseInt(casesQty, 10) : 0;
    const pallets = palletsQty.trim() ? parseInt(palletsQty, 10) : 0;
    if (!Number.isFinite(cases) || cases < 0) return toast.error("Cases must be ≥ 0");
    if (!Number.isFinite(pallets) || pallets < 0) return toast.error("Pallets must be ≥ 0");
    const wantsExpiry = !!activeLine?.lot;
    if (wantsExpiry && expiry && !/^\d{4}-\d{2}-\d{2}$/.test(expiry)) {
      return toast.error("Expiry must be YYYY-MM-DD");
    }
    setBusy(true);
    try {
      const res: any = await recordFn({
        data: {
          asnId,
          asnLineId: lineId,
          locationId,
          qty: n,
          damageQty: dmg,
          condition: dmg > 0 ? "damaged" : "good",
          expiryDate: wantsExpiry && expiry ? expiry : null,
          casesReceived: cases,
          palletsReceived: pallets,
        },
      });
      toast.success(`Received ${n} · ${activeSku?.sku_code ?? "SKU"} → ${locationCode}`);
      await Promise.all([
        refetch(),
        qc.invalidateQueries({ queryKey: ["rf-asn", asnId] }),
        qc.invalidateQueries({ queryKey: ["rf-asns"] }),
      ]);
      // Serial capture is required when SKU policy says so. Park the user
      // on the serials step instead of completing the receipt.
      if (activeSku?.track_serials && res?.receiptLineId) {
        setPendingReceiptLineId(res.receiptLineId);
        setSerialsTarget(n);
        setSerials([]);
        setStep("serials");
        setBusy(false);
        return;
      }
      reset();
      if (res?.asnStatus === "received") {
        toast.message("ASN fully received");
        nav({ to: "/app/rf/receiving" });
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Receive failed");
    } finally {
      setBusy(false);
    }
  }

  function handleSerialScan(code: string) {
    const trimmed = code.trim();
    if (!trimmed) return;
    if (serials.includes(trimmed)) {
      toast.error("Already scanned");
      return;
    }
    if (serials.length >= serialsTarget) {
      toast.error(`Already captured ${serialsTarget}`);
      return;
    }
    setSerials((s) => [...s, trimmed]);
  }

  async function submitSerials() {
    if (!pendingReceiptLineId) return;
    if (serials.length !== serialsTarget) {
      return toast.error(`Need ${serialsTarget} serials (have ${serials.length})`);
    }
    setBusy(true);
    try {
      await serialsFn({ data: { receiptLineId: pendingReceiptLineId, serials } });
      toast.success(`Captured ${serials.length} serials`);
      reset();
    } catch (e: any) {
      toast.error(e?.message ?? "Serial capture failed");
    } finally {
      setBusy(false);
    }
  }

  const open = (lines as any[]).filter((l) => (l.received_qty ?? 0) < (l.expected_qty ?? 0));

  // Drop-into-action: when the worker taps a Receive task from My Work, they
  // expect to land in the next physical step, not on a "pick a line" picker.
  // Auto-advance to the first open line regardless of line count — the
  // worker can switch lines from the location step via "Switch line".
  useEffect(() => {
    if (step !== "line" || lineId) return;
    if (open.length >= 1) {
      pickLine(open[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, lineId, open.length]);

  // Accessorials sheet (Sprint 35 M2 — dock real-world capture).
  const [accOpen, setAccOpen] = useState(false);
  const [detentionMin, setDetentionMin] = useState("");
  const [unloadDollars, setUnloadDollars] = useState("");
  const [unloadKind, setUnloadKind] = useState<
    "lumper" | "hand_stack" | "floor_load" | "rework" | "other"
  >("lumper");
  const [accNotes, setAccNotes] = useState("");
  const [accBusy, setAccBusy] = useState(false);

  async function submitAccessorials() {
    const minutes = detentionMin.trim() ? parseInt(detentionMin, 10) : 0;
    const dollars = unloadDollars.trim() ? parseFloat(unloadDollars) : 0;
    if ((!Number.isFinite(minutes) || minutes < 0) && (!Number.isFinite(dollars) || dollars < 0)) {
      return toast.error("Enter detention minutes or an unload fee");
    }
    if (minutes === 0 && (!Number.isFinite(dollars) || dollars <= 0)) {
      return toast.error("Nothing to record");
    }
    setAccBusy(true);
    try {
      await accessorialsFn({
        data: {
          asnId,
          detentionMinutes: minutes > 0 ? minutes : undefined,
          unloadFeeCents: dollars > 0 ? Math.round(dollars * 100) : undefined,
          unloadKind: dollars > 0 ? unloadKind : undefined,
          notes: accNotes.trim() || undefined,
        },
      });
      toast.success("Dock accessorials recorded");
      setAccOpen(false);
      setDetentionMin("");
      setUnloadDollars("");
      setAccNotes("");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to record accessorials");
    } finally {
      setAccBusy(false);
    }
  }

  return (
    <RfShell
      title={`ASN ${(asn as any)?.reference ?? ""}`}
      back="/app/rf/receiving"
    >
      <button
        type="button"
        onClick={() => setAccOpen(true)}
        className="flex w-full items-center justify-between rounded-md border bg-card px-3 py-2 text-left active:bg-muted/70"
      >
        <span className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          Record what happened at the dock
        </span>
        <span className="text-[11px] text-muted-foreground">Detention · Unload</span>
      </button>

      {step === "line" && (
        <>
          <ScanInput onScan={handleScan} placeholder="Scan SKU or lot…" size="lg" />
          <div className="text-xs text-muted-foreground px-1">
            {open.length} of {lines.length} lines open
          </div>
          <div className="space-y-2">
            {open.length === 0 ? (
              <RfEmpty>All lines received. Close ASN from desktop.</RfEmpty>
            ) : (
              open.map((l: any) => {
                const sku = skuById.get(l.sku_id);
                const remaining = l.expected_qty - l.received_qty;
                return (
                  <button
                    type="button"
                    key={l.id}
                    onClick={() => pickLine(l.id)}
                    className="flex w-full items-center gap-3 rounded-md border bg-card px-3 py-3 text-left active:bg-muted/70"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono text-sm">{sku?.sku_code ?? "—"}</div>
                      <div className="truncate text-xs text-muted-foreground">{sku?.name ?? ""}</div>
                      {l.lot ? <div className="text-[11px] text-muted-foreground">Lot {l.lot}</div> : null}
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-base font-semibold tabular-nums">{remaining}</div>
                      <div className="text-[10px] text-muted-foreground">remaining</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </>
      )}

      {step === "location" && activeLine && (
        <>
          <div className="rounded-md border bg-muted/30 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs text-muted-foreground">SKU</div>
                <div className="font-mono">{activeSku?.sku_code ?? "—"}</div>
                <div className="text-sm truncate">{activeSku?.name ?? ""}</div>
              </div>
              {open.length > 1 ? (
                <button
                  type="button"
                  onClick={reset}
                  className="shrink-0 rounded-md border bg-card px-2 py-1 text-xs font-medium active:bg-muted/70"
                >
                  Switch line ({open.length})
                </button>
              ) : null}
            </div>
          </div>
          <ScanInput onScan={handleScan} placeholder="Scan bin location…" size="lg" />
          <Button variant="outline" className="w-full" onClick={reset}>
            Cancel
          </Button>
        </>
      )}

      {step === "qty" && activeLine && (
        <>
          <div className="rounded-md border bg-muted/30 p-3 space-y-1">
            <div className="font-mono text-sm">{activeSku?.sku_code}</div>
            <div className="text-xs text-muted-foreground">→ {locationCode}</div>
            <div className="text-[11px] text-muted-foreground">
              Expected {activeLine.expected_qty} · Received {activeLine.received_qty}
            </div>
            {activeSku?.track_serials ? (
              <div className="text-[11px] font-medium text-amber-600">Serial-tracked — scan each unit next</div>
            ) : null}
          </div>
          <Input
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus
            value={qty}
            onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))}
            className="h-14 text-2xl text-center font-mono"
            placeholder="0"
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[11px] text-muted-foreground mb-1">Cases</div>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                value={casesQty}
                onChange={(e) => setCasesQty(e.target.value.replace(/\D/g, ""))}
                className="h-10 text-center font-mono"
                placeholder="0"
              />
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground mb-1">Pallets</div>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                value={palletsQty}
                onChange={(e) => setPalletsQty(e.target.value.replace(/\D/g, ""))}
                className="h-10 text-center font-mono"
                placeholder="0"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[11px] text-muted-foreground mb-1">Damaged qty</div>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                value={damageQty}
                onChange={(e) => setDamageQty(e.target.value.replace(/\D/g, ""))}
                className="h-10 text-center font-mono"
                placeholder="0"
              />
            </div>
            {activeLine?.lot ? (
              <div>
                <div className="text-[11px] text-muted-foreground mb-1">Expiry (YYYY-MM-DD)</div>
                <Input
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value)}
                  className="h-10 text-center font-mono"
                  placeholder="2027-01-31"
                />
              </div>
            ) : null}
          </div>
          {Number(damageQty) > 0 ? (
            <div className="rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 p-2 text-[11px] text-amber-700 dark:text-amber-300">
              Damaged units route to the <strong>damaged</strong> state. If your tenant requires
              a photo, capture it from the desktop receipt detail before closing the ASN.
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={reset} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={confirm} disabled={busy || !qty}>
              <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm
            </Button>
          </div>
        </>
      )}

      {step === "serials" && (
        <>
          <div className="rounded-md border bg-muted/30 p-3 space-y-1">
            <div className="font-mono text-sm">{activeSku?.sku_code}</div>
            <div className="text-xs text-muted-foreground">→ {locationCode}</div>
            <div className="text-[11px] text-muted-foreground">
              Captured {serials.length} of {serialsTarget}
            </div>
          </div>
          <ScanInput onScan={handleSerialScan} placeholder="Scan serial…" size="lg" />
          <div className="max-h-48 overflow-auto rounded-md border bg-card divide-y">
            {serials.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground">No serials yet.</div>
            ) : (
              serials.map((s, i) => (
                <div key={s} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="font-mono">{s}</span>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setSerials((arr) => arr.filter((_, idx) => idx !== i))}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={reset} disabled={busy}>Cancel</Button>
            <Button
              onClick={submitSerials}
              disabled={busy || serials.length !== serialsTarget}
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Submit {serials.length}/{serialsTarget}
            </Button>
          </div>
        </>
      )}

      <Sheet open={accOpen} onOpenChange={setAccOpen}>
        <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>What happened at the dock?</SheetTitle>
            <SheetDescription>
              Record what you saw — driver wait, unload help, anything notable.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div>
              <div className="text-xs font-medium mb-1">Driver wait (minutes)</div>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                value={detentionMin}
                onChange={(e) => setDetentionMin(e.target.value.replace(/\D/g, ""))}
                className="h-11 font-mono"
                placeholder="0"
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                How long the driver waited beyond their appointment window.
              </div>
            </div>
            <div>
              <div className="text-xs font-medium mb-1">Unload charge (USD)</div>
              <Input
                inputMode="decimal"
                value={unloadDollars}
                onChange={(e) =>
                  setUnloadDollars(e.target.value.replace(/[^\d.]/g, ""))
                }
                className="h-11 font-mono"
                placeholder="0.00"
              />
            </div>
            <div>
              <div className="text-xs font-medium mb-1">How was it unloaded?</div>
              <Select value={unloadKind} onValueChange={(v) => setUnloadKind(v as any)}>
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lumper">Lumper</SelectItem>
                  <SelectItem value="hand_stack">Hand-stack</SelectItem>
                  <SelectItem value="floor_load">Floor-loaded</SelectItem>
                  <SelectItem value="rework">Rework</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs font-medium mb-1">Notes (optional)</div>
              <Input
                value={accNotes}
                onChange={(e) => setAccNotes(e.target.value)}
                className="h-10"
                placeholder="e.g. driver arrived 90m late"
                maxLength={500}
              />
            </div>
          </div>
          <SheetFooter className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => setAccOpen(false)} disabled={accBusy}>
              Cancel
            </Button>
            <Button onClick={submitAccessorials} disabled={accBusy}>
              <CheckCircle2 className="h-4 w-4 mr-1" /> Record
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </RfShell>
  );
}